import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet, apiPost } from "@/api/client";
import { Button } from "@/components/ui";
import { useSession } from "@/session/SessionProvider";

const pendingSchema = z.object({
  pendingLink: z
    .object({
      provider: z.enum([
        "google",
        "discord",
        "apple",
        "facebook",
        "metamask",
        "phantom",
      ]),
      expiresIn: z.number(),
      requiresSignIn: z.boolean(),
    })
    .nullable()
    .optional(),
});
const labels = {
  google: "Google",
  discord: "Discord",
  apple: "Apple",
  facebook: "Facebook",
  metamask: "MetaMask",
  phantom: "Phantom",
};

/** A verified provider proof never silently attaches to the account that next
 * logs in. The user sees the target account and explicitly confirms connection. */
export function PendingProviderConnection() {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const key = ["session", "pending-provider", session?.playerId];
  const pending = useQuery({
    queryKey: key,
    enabled: Boolean(session),
    queryFn: () => apiGet("/session/providers", pendingSchema),
    retry: false,
    staleTime: 0,
  });
  const connect = useMutation({
    mutationFn: () =>
      apiPost<{ connected: boolean }>("/session/providers/connect", {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["session", "pending-provider"],
      });
    },
  });
  const proof = pending.data?.pendingLink;
  if (!session || !proof || proof.expiresIn <= 0) return null;
  return (
    <aside
      className="provider-connection-banner"
      aria-label="Confirm sign-in method connection"
    >
      <div>
        <strong>
          Connect {labels[proof.provider]} to {session.displayName}?
        </strong>
        <p>
          You verified this provider before signing in. Connecting adds a
          sign-in method to your shared MonkeyTilt account; it does not create a
          new wallet or move your horses.
        </p>
        {connect.error ? (
          <p role="alert">
            Connection could not be confirmed. Sign in again and retry the
            provider flow. Nothing was moved.
          </p>
        ) : null}
      </div>
      <Button
        disabled={connect.isPending}
        onClick={() => connect.mutate()}
        type="button"
      >
        {connect.isPending
          ? "Connecting…"
          : `Connect ${labels[proof.provider]}`}
      </Button>
    </aside>
  );
}
